import React, { memo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';

import { ButtonTypes, IconSizes } from 'constants/Constants';
import ToolTip from 'components/common/ToolTip/ToolTip';
import Icon from 'components/common/Icon/Icon';

import styles from './IconButton.module.scss';

const s = classNames.bind(styles);

const IconButton = ({
	className,
	disabled,
	fixedIconColor,
	icon,
	iconColor,
	iconSize,
	iconClassName,
	onClickCallback,
	onDarkBg,
	rotateIcon,
	rotateIconByDeg,
	withSeparatorRight,
	withSeparatorLeft,
	withSmallSeparator,
	withLargeSeparator,
	size,
	toolTip
}) => {
	/**
	|--------------------------------------------------
	| @function onClicked
	|--------------------------------------------------
	*/
	const onClicked = event => {
		if (event) {
			event.stopPropagation();
		}

		if (onClickCallback) {
			onClickCallback();
		}
	};

	/**
	|--------------------------------------------------
	| @function getButton markup
	|--------------------------------------------------
	*/
	const getButton = () => (
		<button
			onClick={onClicked}
			className={s('container', {
				container_small: size === ButtonTypes.SMALL,
				container_medium: size === ButtonTypes.MEDIUM,
				container_large: size === ButtonTypes.LARGE,
				container_withSeparatorRight: withSeparatorRight,
				container_withSeparatorLeft: withSeparatorLeft,
				container_withSmallSeparator: withSmallSeparator,
				container_withLargeSeparator: withLargeSeparator,
				container_onDarkBg: onDarkBg,
				container_disabled: disabled,
				container_withoutCallback: !onClickCallback,
				[className]: className
			})}
		>
			{icon && (
				<Icon
					icon={icon}
					color={iconColor}
					fixedIconColor={fixedIconColor}
					size={iconSize || IconSizes.AUTO}
					className={iconClassName}
					rotateIcon={rotateIcon}
					rotateIconByDeg={rotateIconByDeg}
				/>
			)}
		</button>
	);

	return toolTip ? (
		<ToolTip text={toolTip} toolTipClassName={s({ toolTipWithSeparator: withSeparatorRight || withSeparatorLeft })}>
			{getButton()}
		</ToolTip>
	) : (
		getButton()
	);
};

IconButton.propTypes = {
	className: PropTypes.string,
	icon: PropTypes.string,
	iconColor: PropTypes.string,
	rotateIcon: PropTypes.bool,
	rotateIconByDeg: PropTypes.number,
	fixedIconColor: PropTypes.bool, // If we dont want to change color of icon on hover
	onDarkBg: PropTypes.bool, // If icon is displayed on a dark background we need to set a fixed grey hover color for it to bleed through
	iconClassName: PropTypes.string,
	onClickCallback: PropTypes.func,
	// The size of the button
	size: PropTypes.oneOf([ButtonTypes.SMALL, ButtonTypes.MEDIUM, ButtonTypes.LARGE]),
	// Set to implicitly control the icon size. Per default the icon inside the component stretches full width and height of the button.
	iconSize: PropTypes.oneOf([IconSizes.SMALL, IconSizes.MEDIUM, IconSizes.LARGE]),
	disabled: PropTypes.bool,
	withSeparatorRight: PropTypes.bool,
	withSeparatorLeft: PropTypes.bool,
	withSmallSeparator: PropTypes.bool,
	withLargeSeparator: PropTypes.bool,
	toolTip: PropTypes.string
};

export default memo(IconButton);
